/**
 * What the thread says about where the form-first flow is.
 *
 * Derived from the persisted messages, never stored: cards, answers, and the
 * `stale` / `chosen` marks on concept cards are all rows, so a reload lands in
 * exactly the same place and there is no second source of truth to disagree with.
 */
import type { AgentChatMessage } from "../../types";
import type { Concept, ConceptCardData, FormId } from "./formTypes";

export type FormFlow = {
  /** The forms that have been submitted at least once. */
  answered: Record<FormId, boolean>;
  /** The newest submitted answer of each form: the only one that can be re-opened. */
  latestAnswerId: Record<FormId, string | null>;
  /** A concept was picked. */
  conceptChosen: boolean;
  /** Every concept id in the thread, stale or not — what a live card is deduped against. */
  persistedConceptIds: Set<string>;
  /** A current (non-stale) concept exists: "Generar otros" has something to replace. */
  hasCurrentConcepts: boolean;
};

export const emptyFormFlow = (): FormFlow => ({
  answered: { BRIEF: false, LOGISTICS: false },
  latestAnswerId: { BRIEF: null, LOGISTICS: null },
  conceptChosen: false,
  persistedConceptIds: new Set(),
  hasCurrentConcepts: false,
});

export const deriveFormFlow = (messages: AgentChatMessage[]): FormFlow => {
  const flow = emptyFormFlow();
  for (const message of messages) {
    if (message.formAnswer) {
      flow.answered[message.formAnswer.formId] = true;
      // Messages are oldest first, so the last one seen wins.
      flow.latestAnswerId[message.formAnswer.formId] = message.id;
    }
    if (message.concept) {
      flow.persistedConceptIds.add(message.concept.concept.id);
      if (message.concept.chosen) flow.conceptChosen = true;
      if (!message.concept.stale) flow.hasCurrentConcepts = true;
    }
  }
  return flow;
};

/**
 * The Brief can be edited while the session still accepts `submit_brief` (until a
 * concept is chosen) — and only its newest submission: an older one is history, and
 * re-opening it would fork the brief. The server says whether the action is open; the
 * thread says which submission is the newest.
 */
export const canEditBrief = (
  flow: FormFlow,
  messageId: string,
  canSubmitBrief: boolean,
): boolean => canSubmitBrief && flow.latestAnswerId.BRIEF === messageId;

/** Live concepts the thread does not have yet, in arrival order. */
export const unpersistedConcepts = (
  live: Concept[],
  persistedIds: Set<string>,
): Concept[] => live.filter((concept) => !persistedIds.has(concept.id));

/** A live concept, drawn like a persisted one that is neither stale nor chosen. */
export const liveConceptCard = (concept: Concept): ConceptCardData => ({
  concept,
  stale: false,
  chosen: false,
});

/**
 * Is the script still being written?
 *
 * True from the moment a concept is chosen until `SCRIPT_READY`, but only while a job
 * is actually running: after a reload the live "script ready" fact is gone, and a
 * finished job is what says the script is there. A job that failed is not "waiting" —
 * the person is let through and the backend says what is missing.
 */
export const isWaitingForScript = (
  flow: FormFlow,
  scriptReady: boolean,
  jobRunning: boolean,
): boolean => flow.conceptChosen && !scriptReady && jobRunning;

/**
 * The one line that says what the flow is doing right now, or null.
 *
 * Shown and announced (`aria-live`) so a stage change — concepts arriving, the script
 * being written, the script done — is something a person who cannot see the cards
 * appear still hears. Only the form-first stages have a caption: a plain chat turn is
 * already narrated by its own thinking indicator.
 */
export const stageCaption = ({
  flow,
  live,
  jobRunning,
}: {
  flow: FormFlow;
  live: {
    concepts: { items: unknown[]; total: number | null };
    scriptReady: boolean;
  };
  jobRunning: boolean;
}): string | null => {
  if (flow.answered.LOGISTICS) return null;
  if (flow.conceptChosen) {
    if (jobRunning && !live.scriptReady) return "Escribiendo el guion…";
    if (live.scriptReady) {
      return "El guion está listo. Completa los últimos detalles para terminar.";
    }
    return null;
  }
  if (jobRunning && flow.answered.BRIEF) {
    const { items, total } = live.concepts;
    if (total !== null && items.length) {
      return `Generando conceptos… ${items.length} de ${total}`;
    }
    return "Generando conceptos…";
  }
  return null;
};
